<template>
  <div
    class="accountants-customer-selector"
    :class="['size-' + styleOptions.size, { search: withSearch, selectable: selectable }]"
    :style="{ height: styleOptions.height, width: styleOptions.width }"
  >
    <!-- Search selection bar -->
    <div class="selector-search" v-if="withSearch">
      <b-form-input v-model="searchModel" :placeholder="$gettext('Search')" />
      <font-awesome-icon v-if="searchModel && searchModel != ''" @click="searchModel = ''" icon="fa-solid fa-circle-xmark" class="reset-search" />
      <font-awesome-icon v-else aria-hidden="true" icon="fa-solid fa-search" />
    </div>
    <hr v-if="withSearch" class="mb-0" />
    <!-- Selector list -->
    <overlay-scrollbars :options="scrollBarsOptions" ref="customerSelectorScrollbar">
      <ul
        class="selector-list"
        tabindex="0"
        @keydown.prevent.stop="onListKeydown"
        ref="listEl"
      >
        <li
          v-for="(customer, index) in customerList"
          :key="customer.id + ' ' + index"
          ref="items"
          class="list-item"
          :class="{ selected: isSelected(customer), active: index === activeIndex }"
          @click="onItemClick(customer, index)"
        >
          <input
            v-if="selectable"
            type="checkbox"
            :checked="isSelected(customer)"
            @change.stop="toggleSelection(customer)"
            @click.stop
          />
          <div class="item-content">
            <div v-if="showLogo" class="item-logo">
              <b-avatar
                :src="customer.customer.logo ? customer.customer.logo : ''"
                :text="$gettext('No logo')"
                :size="logoComputedSize"
              />
            </div>
            <div class="item-text">
              <div class="item-text--name">{{ customer.name }}</div>
              <div v-if="showEmail" class="item-text--email">{{ customer.customer_email }}</div>
            </div>
          </div>
        </li>
        <li v-if="!loading && customerList.length < 1" class="list-item">
          {{ $gettext('No companies found.')}}
        </li>
        <li v-if="loading || nextListUrl" class="list-item spinner">
          <LoadingSpinner button-spinner size="sm" />
        </li>
      </ul>
    </overlay-scrollbars>
    <!-- Confirm selection bar -->
    <hr v-if="selectable" class="mt-0" />
    <div v-if="selectable" class="selector-actions">
      <GekkoButton 
        @click="clearSelection"
        variant="link" 
        :text="$gettext('Clear')"
        class="p-0"
        :size="styleOptions.size"
      />
      <GekkoButton 
        :disabled="selectedCount === 0"
        @click="confirmSelection"
        :text="$gettext('Ok') + '(' + selectedCount + ')'" 
        :size="styleOptions.size"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { debounce } from "@/utils/";
import GekkoButton from '@/components/GekkoButton'
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
  name: 'CustomerSelector',
  components: {
    LoadingSpinner,
    GekkoButton
  },
  props: {
    value: { type: String, default: null }, // external model (optional)
    withSearch: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    styleOptions: {
      type: Object,
      default: () => {
        return {
          size: 'sm',
          height: '100%',
          width: '100%'
        }
      }
    },
    showLogo: {
      type: Boolean,
      default: false
    },
    showEmail: {
      type: Boolean,
      default: false
    },
    focusable: {
      type: Boolean,
      default: false
    },
    excludeIds: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      search_by_name: '',
      loading: false,
      debounceTime: 300,
      scrollBarsOptions: {
        scrollbars: { autoHide: 'never' },
        overflowBehavior: { y: 'scroll', x: 'hidden' },
        callbacks : {
          onScroll: this.handleScroll
        }
      },
      rawCustomerList: [],
      nextListUrl: null,
      selectedMap: {}, // { [id]: true }
      activeIndex: -1
    }
  },
  computed: {
    // single source of truth for search text
    searchModel: {
      get() {
        return this.value != null ? this.value : this.search_by_name
      },
      set(val) {
        if (this.value != null) this.$emit('input', val) // update parent when controlled
        this.search_by_name = val                        // keep local in sync either way
      }
    },
    customerList() {
      return this.rawCustomerList.filter(c => {
        const companyId = c.customer?.companysettings?.company
        return !this.excludeIds.includes(companyId)
      })
    },
    filters () {
      return { search: this.searchModel }
    },
    selectedCustomers () {
      const map = this.selectedMap
      return this.customerList.filter(c => !!map[c.id])
    },
    selectedCount () {
      return this.selectedCustomers.length
    },
    logoComputedSize () {
      let size = '26px'
      if (this.styleOptions.size === 'md') {
        size = '34px'
      }
      return size
    }
  },
  watch: {
    searchModel(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loading = true
        this.nextListUrl = null
        this.rawCustomerList = []
        this.selectedMap = {}
        this.getCustomersDebounced()
      }
    }
  },
  created () {
    this.setupData()
    this.getCustomersDebounced = debounce(() => {
      this.getCustomers()
    }, 300)
  },
  mounted () {
    this.osInstance = this.$refs.customerSelectorScrollbar.osInstance()
  },
  methods: {
    ...mapActions({ 
      getAccountantConnections: 'accountants/getAccountantConnections',
      getAccountantNextListConnections: 'accountants/getAccountantNextListConnections'
    }),
    setupData () {
      this.getCustomers()
    },
    getCustomers() {
      this.loading = true

      const request = this.nextListUrl
        ? this.getAccountantNextListConnections(this.nextListUrl)
        : this.getAccountantConnections(this.filters)

      request.then((response) => {
        const results = response.data.results.map((item) => {
          item.customer_name = item.customer.created_by.display_name
          item.customer_email = item.customer.email
          return item
        })

        if (this.nextListUrl) {
          this.rawCustomerList = this.rawCustomerList.concat(results)
        } else {
          this.rawCustomerList = results
        }

        this.nextListUrl = response.data.next
      }).finally(() => {
        this.loading = false
      })
    },
    handleScroll () {
      const scrollEndThreshold = 0.9
      if (this.loading) return
      if (this.osInstance.scroll().ratio.y >= scrollEndThreshold && this.nextListUrl) {
        this.getCustomersDebounced()
      }
    },
    onItemClick (customer, index) {
      this.activeIndex = index
      if (this.selectable) {
        this.toggleSelection(customer)
      } else {
        this.$emit('customer-selected', customer)
      }
    },
    focusOnList (active = null) {
      // focus on the list if focusable
      if (this.$refs.listEl) {
        this.$refs.listEl.focus()
        if (active != null) {
          this.activeIndex = active
        }
      }
    },
    onListKeydown (e) {
      if (!this.customerList.length || !this.focusable) return

      this.focusOnList()

      if (e.key === 'ArrowDown') {
        this.moveActive(1)
      } else if (e.key === 'ArrowUp') {
        this.moveActive(-1)
      } else if (e.key === 'Enter') {
        const customer = this.customerList[this.activeIndex]
        if (customer) {
          if (this.selectable) {
            this.toggleSelection(customer)
          } else {
            this.$emit('customer-selected', customer)
          }
        }
      } else if (e.key === ' ' || e.code === 'Space') {
        // Space toggles selection only when selectable
        if (this.selectable) {
          const customer = this.customerList[this.activeIndex]
          if (customer) this.toggleSelection(customer)
        }
      }
    },
    moveActive(delta) {
      const len = this.customerList.length
      if (!len) return

      let next = this.activeIndex + delta

      // clamp between 0 and len - 1
      if (next < 0) next = 0
      if (next >= len) next = len - 1

      this.activeIndex = next
      this.scrollToActive()
    },
    scrollToActive () {
      this.$nextTick(() => {
        const items = this.$refs.items || []
        const el = items[this.activeIndex]
        if (!el) return

        // Works fine with OverlayScrollbars: scroll the element into the viewport of the scroll container
        try {
          el.scrollIntoView({ block: 'nearest' })
        } catch (_) {
          // Fallback: use OverlayScrollbars API if needed
          const os = this.osInstance
          if (os) {
            const itemTop = el.offsetTop
            const itemBottom = itemTop + el.offsetHeight
            const { scrollTop, clientHeight } = os.elements().viewport
            if (itemTop < scrollTop) {
              os.scroll({ y: itemTop })
            } else if (itemBottom > scrollTop + clientHeight) {
              os.scroll({ y: itemBottom - clientHeight })
            }
          }
        }
      })
    },
    toggleSelection (customer) {
      const id = customer.id
      const next = !this.selectedMap[id]
      this.$set(this.selectedMap, id, next)
    },
    isSelected (customer) {
      return !!this.selectedMap[customer.id]
    },
    clearSelection () {
      this.selectedMap = {}
    },
    confirmSelection () {
      this.$emit('customer-selected-multiple', this.selectedCustomers)
      this.clearSelection()
    }
  }
}
</script>

<style lang="scss">
.accountants-customer-selector {
  $selectorSearchHeight: 24px;
  $selectorConfirmHeight: 31px;
  height: 100%;
  width: 100%;
  position: relative;
  border-radius: $radiusLg;
  overflow: hidden;
  .os-host { 
    height: 100%;
    .os-content {
      padding: 0 !important;
    }
  }
  &.search {
    .os-host {
      // 0.5rem for <hr> margin
      height: calc(100% - calc(0.5rem + #{$selectorSearchHeight}));
    }
  }

  &.selectable {
    .os-host {
      // 0.5rem for <hr> margin
      height: calc(100% - calc(0.5rem + #{$selectorConfirmHeight}));
    }
  }
  &.search.selectable {
    .os-host {
      // 1rem for 2 * <hr> margin
      height: calc(100% - calc(1rem + calc(#{$selectorSearchHeight} + #{$selectorConfirmHeight})));
    }
  }
  .selector-search {
    height: $selectorSearchHeight;
    display: flex;
    flex: 1;
    align-items: center;
    padding: 0 5px;

    .svg-inline--fa {
      font-size: 14px;
      color: #5e6c84;
      &.reset-search {
        color: $colorBrand;
        cursor: pointer;
      }
    }
    input {
      height: 100%;
      padding: 0px;
      text-overflow: ellipsis;
      box-shadow: none;
      background: transparent;
      border: none;
      &:focus { box-shadow: none; background: transparent; }
      &::placeholder { color: $colorUnassigned; }
    }
  }
  hr {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  ul.selector-list {
    margin: 0;
    padding: 0;
    &:focus-visible {
      outline: none;
    }
    li.list-item {
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      position: relative;
      min-height: 2rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      .item-content {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        .item-text {
          display: flex;
          flex-direction: column;
          &--name, &--email {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
            position: relative;
          }
          &--email {
            color: $colorUnassigned;
            font-size: 0.85em;
          }
        }
      }
      &.spinner {
        height: 75px;
      }
      &.selected {
        background: rgba(34, 172, 138, 0.07);
      }
      &:hover:not(.selected):not(.spinner), &.active { 
        background-color: #f1f1f1;
      }
      &:last-child { margin-bottom: 0; }
    }
  }
  .selector-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.5rem;
  }
  &.size-sm {
    ul.selector-list {
      font-size: 14px;
    }
  }
  &.size-md {
    ul.selector-list {
      font-size: 1rem;
      li.list-item {
        padding: 0.5rem 0.75rem;
        .item-content {
          gap: 0.5rem;
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
</style>
